import { debounce, formatToTitleCase } from "../../src/utils/helpers";
import { PositionsService } from "../services/positions-service";
import { MODEL_DATA_STATUS } from "./base-model";
import { FiSubView, UiStateModel, UiStateModelParam } from "./ui-state-model";
import { VesselsTrackingModel } from "./vessels-tracking-model";
import { FilterUtils } from "../../local_modules/voy-global-filters/src/filter-utils";
import { regionsMap } from "./terminals-model-impl";
import { PortViewModelBase } from "./port-view-model";
import { TerminalsModel } from "./terminals-model";
import { GSISEntry, ListEntry } from "./types/port-view-model.types";
import { TerminalInfo } from "./types/terminals-model.types";
import { VesselInfo } from "./types/vessels-model.types";
import { IMOsCurrentScheduleState } from "./types/voyage-schedules-model.types";
import { ShoreContextEvent } from "./types/voyage-shore-context-model.types";
import { VesselsModel } from "./vessels-model";
import { VoyageSchedulesModel } from "./voyage-schedules-model";
import { DbModel } from "./db-model";
import { ShipType, VesselPositionData } from "../services/types/position-service.types";


export class PortViewModelImpl extends PortViewModelBase {

    private _ocl: string|undefined;
    private _port: string|undefined;
    private _terminalsByCity: TerminalInfo[] = [];
    private _portFilter: string[] = [];
    private _unLocodeFilter: string[] = [];
    private _aisData: Map<number, VesselPositionData> = new Map();
    private _scheduleData: IMOsCurrentScheduleState[] = [];
    private _scheduleDataDtls: Map<number, GSISEntry[]> = new Map();
    private _list: ListEntry[] = [];
    private _debouncedUpdateFromPositionsService;
    _stopFetchingData:boolean = false;

    public unLocode2Rkst: Map<string, string> = new Map();

    constructor() {
        super();

        this._getSchedulesData = this._getSchedulesData.bind(this);
        this._getAISData = this._getAISData.bind(this);
        this._postProcessing = this._postProcessing.bind(this);
        this._updateFilters = this._updateFilters.bind(this);
        this._updateTerminals = this._updateTerminals.bind(this);
        this._debouncedUpdateFromPositionsService = debounce(this._updateFromPositionsService.bind(this), 5000);
        
        this._connect();
    }

    setOclAndPort(ocl?: string, port?: string) {
        this._stopFetchingData = false;
        this._ocl = ocl;
        this._port = ocl ? undefined : port; 
        this._updateFilters();
    }

    stopFetchingData() {
        this._stopFetchingData = true;
    }

    private _connect() {
        VoyageSchedulesModel!.addEventListener('schedule-states-updated', this._getSchedulesData);
        TerminalsModel?.addEventListener('terminals-updated', this._updateFilters);
        VesselsModel?.addEventListener('vessels-updated', this._postProcessing);
        UiStateModel!.addEventListener(`${UiStateModelParam.PORT}-change`, this._updateTerminals)
        UiStateModel!.addEventListener(`${UiStateModelParam.PortRegion}-change`, this._updateTerminals)
        UiStateModel!.addEventListener(`${UiStateModelParam.Search}-change`, this._postProcessing);
    }

    private _updateTerminals(evt: Event) {
        this._scheduleDataDtls.clear();
        this._port = (<CustomEvent>evt).detail.value;
        this._updateFilters();
    }

    get traffic() {
        return this._list;
    }

    get terminals() {
        return this._terminalsByCity;
    }

    private _getItemSortTime(item: ListEntry): number {
        if (item.gsis) {
            // TODO consider why there might be more than one ScheduleEntry
            return item.gsis[0].arrival.arrivalPortCall?.GSIS?.actuals.arrivalAt
                ? (new Date(item.gsis[0].arrival.arrivalPortCall?.GSIS?.actuals.arrivalAt)).getTime()
                : item.gsis[0].arrival.eta;
        } else if (item.ais) {
            let {etaScheduled, etaCalc} = item.ais;
            return (new Date(etaCalc || etaScheduled || Number.MAX_SAFE_INTEGER)).getTime();
        } else {
            return (new Date(Number.MAX_SAFE_INTEGER)).getTime();
        }
    };


    private _postProcessing() {
        // take a starting point in GSIS, which is our most reliable schedule source
        this._list = [...this._scheduleDataDtls.entries()].map(([imo, gsis]) =>  {
            const vessel: VesselInfo = VesselsModel?.getVessel(imo)!;
            let vesselName;
            if (vessel) {
                vesselName = vessel.vesselName;
            } else {
                vesselName = VesselsModel!.getVesselName(imo);
            }
            return { imo, gsis, ais: this._aisData.get(imo), name: vesselName, isMaerskVessel: vessel?.operatorCode === 'MSK', vesselType: vessel?.vesselType };
        });

        // and supplement with information from AIS
        for (let [imo, ais] of this._aisData.entries()) {
            const vessel: VesselInfo = VesselsModel?.getVessel(imo)!;
            let vesselName;
            if (vessel) {
                vesselName = vessel.vesselName;
            } else {
                vesselName = VesselsModel!.getVesselName(imo)!;
            }
            if (!this._scheduleDataDtls.has(imo)) {
                this._list.push({ imo, ais, name: formatToTitleCase(vesselName), isMaerskVessel: vessel?.operatorCode === 'MSK', vesselType: vessel?.vesselType });
            }
        }

        //get all q paramethers
        const allFilters = FilterUtils.stringToFilter(UiStateModel?.getValue('q') ?? "");
        const portView = UiStateModel?.getValue(UiStateModelParam.FISubView);

         //fins paramether for selected terminals
        const terminalTrafficFilters = allFilters?.commands.find(f => f.cmd === 'tml')?.vals;
        
        if(terminalTrafficFilters && terminalTrafficFilters.length > 0) {
            //check if vessel arrival at or departure for selected terminals
            this._list = this._list.filter(el => {
                return el.gsis?.find(el => terminalTrafficFilters.includes(el.arrival.arrivalTerminalCode.toLowerCase()) 
                    || ( el.departure && terminalTrafficFilters.includes(el.departure.arrivalTerminalCode.toLowerCase()))); 
            })
        }
  
        this._list.sort((a, b) => this._getItemSortTime(a) - this._getItemSortTime(b));
        const imos = this._list.map(el => el.imo);

        if(this._stopFetchingData) {
            return;
        }

        if(portView === FiSubView.PortView || portView === FiSubView.PortsByRegionView){
            VesselsTrackingModel!.setIMOList(imos);
        }
       
        this.dispatchEvent(new CustomEvent(ShoreContextEvent.VesselsChange));

        this.dispatchEvent(new Event('port-view-model-updated'));
        this._debouncedUpdateFromPositionsService();
    }

    private async _getAISData() {
        if (this._unLocodeFilter.length === 0) return;

        const vessels = await DbModel!.getAllPositionsRecords(); 

        const aisData = vessels.filter(vessel => vessel.vesselType === 'Cargo' ||  vessel.vesselType === ShipType.Cargo );
        const now = Date.now();

        this._aisData = new Map();
        aisData.forEach(ovi => {

            let relevant = false;
            if (ovi?.currUnlocode
                && this._unLocodeFilter.indexOf(ovi?.currUnlocode.toLowerCase()) !== -1) {
                let ts = (new Date(ovi.etaScheduled)).getTime();
                relevant = Math.abs(ts - now) < 720 * 60 * 60 * 1000; // been identified as being in port within the last 30 days
            } else if (ovi?.nextUnlocode
                && this._unLocodeFilter.indexOf(ovi?.nextUnlocode.toLowerCase()) !== -1) {
                
                let ts = (new Date(ovi.etaCalc)).getTime();
                relevant = Math.abs(ts - now) < 720 * 60 * 60 * 1000; // been identified as arriving with +/- 30 days from now
            }

            if (relevant) {
                this._aisData.set(ovi.imo, ovi);
            }
        });

        this._postProcessing();
    }

    private async _getSchedulesData() {
        const schedule = VoyageSchedulesModel?.currentScheduleStates;
        if (schedule) {
            this._scheduleData = schedule.filter((si) => {
                return this._portFilter.indexOf(si.schedule.startPort.substring(0, 5)) !== -1
                    || this._portFilter.indexOf(si.schedule.endPort.substring(0, 5)) !== -1;
            });
            const now = Date.now();

            this._postProcessing();
            for (let item of this._scheduleData) {
                const request = VoyageSchedulesModel?.getScheduleFor(item.imoNo, 3);
                if(this._stopFetchingData) {
                    return;
                }
                let dtls = await request;
            
                if (!dtls) continue;

                let elm;
                let res: GSISEntry[] = [];
                while (elm = dtls.shift()) {
                    if (this._portFilter.indexOf(elm.arrivalTerminalCode.substring(0, 5)) !== -1) {
                        const next = dtls.length && this._portFilter.indexOf(dtls[0].departureTerminalCode.substring(0, 5)) !== -1
                            ? dtls[0] // handling departure and arrival to the same port
                            : dtls.shift();
                        res.push({arrival: elm, departure: next});
                    }
                }
                res = res.filter((e) => {
                    const arriveAtTs = (new Date(e.arrival.arrivalAt!)).getTime();
                    const departureAtTs = (new Date(e.departure?.departureAt!)).getTime();

                    return (arriveAtTs  > now && arriveAtTs < now + 720*60*60*1000)
                        || (departureAtTs > now && departureAtTs < now + 720*60*60*1000)
                        || (e.arrival?.arrivalPortCall?.GSIS?.actuals.arrivalAt && !e.departure?.departurePortCall?.GSIS?.actuals.departureAt);
            
                });
    
                if (res.length === 0) continue;

                this._scheduleDataDtls.set(item.imoNo, res);
                this._postProcessing();
            }
        }
    }

    private async _updateFromPositionsService() {
        const positions = await DbModel!.getAllPositionsRecords();
        const mapPositions = new Map(positions.map((p) => [p.imo, p]));

        this._list = this._list.map(el => {
            el.pos = mapPositions.get(el.imo);
            return el;
        })
      
        this.dispatchEvent(new Event('port-view-model-updated'));
    }

    private async _updateFilters() {
        if (!TerminalsModel || TerminalsModel.status === MODEL_DATA_STATUS.NOT_READY) return;

        this._terminalsByCity = (this._port && TerminalsModel.getTerminalsByCity(this._port)) || [];

        const oclTerminals = this._ocl ? this._mapOCLtoTerminals(this._ocl) : undefined;
        this._portFilter = oclTerminals ? [...(new Set(oclTerminals.map((t) => t.cityId))).values()] : [this._port!];

        this._unLocodeFilter = (
            oclTerminals
            ? [...(new Set(oclTerminals.map((t) => t.unLocode!))).values()]
            : this._mapRKST2Unlocode(this._terminalsByCity)
        ).map((u) => u.toLowerCase());

        this.unLocode2Rkst.clear();
        for (let terminal of TerminalsModel.terminals.values()) {
            if (terminal.unLocode && !this.unLocode2Rkst.has(terminal.unLocode)) {
                this.unLocode2Rkst.set(terminal.unLocode, terminal.locationId.substring(0, 5));
            }
        }

        this._getAISData();
        this._getSchedulesData();
    }

    private _mapRKST2Unlocode( terminals: TerminalInfo[] ): string[] {
        return terminals.length > 0 && terminals[0].unLocode ? [terminals[0].unLocode] : [];
    }

    private _mapOCLtoTerminals(ocl: string): TerminalInfo[] {
        const region = regionsMap.find(el => el.key.toLowerCase() === ocl.toLowerCase());

        const terminals = new Set(
            [...TerminalsModel!.terminals.values()]
            .filter((t) => region?.locations.includes(t.locId!))
        );

        return [...terminals.values()];
    }
}

